import * as enabledApi from '@shared/api/enabledPlayApi';
import UpdateAuthClientRequest from '@shared/models/authentication/UpdateAuthClientRequest';
import { AvailableCustomControlsModel } from '@shared/models/devices/AvailableCustomControlsModel';
import DeviceTypeModel from '@shared/models/devices/DeviceTypeModel';
import UpdateDeviceTypeRequest from '@shared/models/devices/UpdateDeviceTypeRequest';
import ISearchParams from '@shared/models/ISearchParams';
import InviteOrganizationMemberRequest from '@shared/models/organizations/InviteOrganizationMemberRequest';
import { LicenseKeyFilterTypes } from '@shared/models/organizations/LicenseKeyFilterTypes';
import LicenseKeyModel from '@shared/models/organizations/LicenseKeyModel';
import OrganizationAuthClientModel from '@shared/models/organizations/OrganizationAuthClientModel';
import OrganizationMemberDetailsModel from '@shared/models/organizations/OrganizationMemberDetailsModel';
import OrganizationMemberInviteModel from '@shared/models/organizations/OrganizationMemberInviteModel';
import OrganizationMemberModel from '@shared/models/organizations/OrganizationMemberModel';
import OrganizationModel from '@shared/models/organizations/OrganizationModel';
import OrganizationPrivateProductModel from '@shared/models/organizations/OrganizationPrivateProductModel';
import { UpdateOrganizationMemberRequest } from '@shared/models/organizations/UpdateOrganizationMemberRequest';
import UpdateOrganizationRequest from '@shared/models/organizations/UpdateOrganizationRequest';
import { ProductModel } from '@shared/models/product/ProductModel';
import InvalidResult from '@shared/models/result/InvalidResult';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import IScopeOption from '@shared/models/scopes/IScopeOption';
import ScopeModel from '@shared/models/scopes/ScopeModel';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { publish, subscribe } from '../helpers/EventCallback';

interface LoadingData {
    organizationMembers: boolean;
    organizations: boolean;
    projects: boolean;
    licenseKeys: boolean;
    invites: boolean;
    scopes: boolean;
    memberships: boolean;
    currentOrg: boolean;
    managedOrgs: boolean;
    managedKeys: boolean;
    products: boolean;
}

interface OrganizationState {
    // State
    currentOrg: OrganizationModel | null;
    currentOrgId: string | null | undefined;
    currentUserEmail: string | null;
    errors: string[];
    invites: { organizationId: string; invites: OrganizationMemberInviteModel[] }[];
    loading: boolean;
    loadingData: LoadingData;
    memberships: OrganizationMemberDetailsModel[];
    organizationMembers: { organizationId: string; members: OrganizationMemberModel[] }[];
    orgAuthClients: OrganizationAuthClientModel[];
    orgAvailableCustomControls: AvailableCustomControlsModel[];
    orgDeviceTypes: DeviceTypeModel[];
    orgManagedLicenseKeys: LicenseKeyModel[];
    orgManagedOrgs: OrganizationModel[];
    orgProducts: OrganizationPrivateProductModel[];
    scopes: IScopeOption[];

    // Actions
    acceptOrganizationInvite: (inviteId: string) => Promise<IResult<OrganizationMemberModel>>;
    activateExistingOrgLicenseKey: (
        organizationId: string,
        request: ActivateLicenseKeyRequest
    ) => Promise<IResult<OrganizationModel>>;
    activateNewOrgLicenseKey: (request: NewOrganizationWithKeyRequest) => Promise<IResult<OrganizationModel>>;
    createOrganizationInvite: (
        organizationId: string,
        request: InviteOrganizationMemberRequest
    ) => Promise<IResult<any> | undefined>;
    createOrgAuthClient: (
        organizationId: string,
        request: UpdateAuthClientRequest
    ) => Promise<IResult<OrganizationAuthClientModel>>;
    createOrgDeviceType: (
        organizationId: string,
        request: UpdateDeviceTypeRequest
    ) => Promise<IResult<DeviceTypeModel>>;
    createManagedOrg: (
        organizationId: string,
        newOrganization: NewOrganizationWithKeyRequest
    ) => Promise<IResult<OrganizationModel>>;
    deleteOrganizationInvite: (organizationId: string, inviteId: string) => Promise<IResult<any> | undefined>;
    deleteOrgAuthClient: (organizationId: string, orgAuthClientId: string) => Promise<IResult<any>>;
    deleteOrgDeviceType: (organizationId: string, orgDeviceTypeId: string) => Promise<IResult<any>>;
    getBillingLink: (organizationId: string) => Promise<string | null>;
    getBillingLinkForOrgByProduct: (organizationId: string, productId: string) => Promise<string | null>;
    getBillingLinkForManagedOrg: (organizationId: string, managedOrgId: string) => Promise<string | null>;
    getOrganizationDetails: (organizationId: string) => Promise<IResult<OrganizationModel>>;
    getOrgAuthClients: (organizationId: string) => Promise<IResult<OrganizationAuthClientModel[]>>;
    getOrgAvailableCustomControls: (organizationId: string, locale: string) => Promise<boolean>;
    getOrgDeviceTypes: (organizationId: string) => Promise<IResult<DeviceTypeModel[]>>;
    getOrgManagedLicenseKeysAvailable: (
        organizationId: string,
        searchParams?: ISearchParams
    ) => Promise<IResult<LicenseKeyModel[]>>;
    getOrgManagedLicenseKeysUsed: (
        organizationId: string,
        searchParams?: ISearchParams
    ) => Promise<IResult<LicenseKeyModel[]>>;
    getOrgManagedOrgsAll: (
        organizationId: string,
        searchParams?: ISearchParams
    ) => Promise<IResult<OrganizationModel[]>>;
    getOrganizationMembership: (organizationId?: string) => OrganizationMemberDetailsModel | undefined;
    getScopes: () => Promise<ScopeModel[]>;
    getTrialUpgradeLink: (organizationId: string, trialId: string) => Promise<string | null>;
    getTrialUpgradeLinkForProduct: (
        organizationId: string,
        trialId: string,
        productId: string
    ) => Promise<string | null>;
    getOrgManagedLicenseKeys: (
        organizationId: string,
        searchParams?: ISearchParams
    ) => Promise<IResult<LicenseKeyModel[]>>;
    getOrganizationProducts: (
        organizationId: string,
        locale: string,
        searchParams?: ISearchParams
    ) => Promise<IResult<OrganizationPrivateProductModel[]>>;
    getOrganizationProductLink: (organizationId: string, productId: string) => Promise<string | null>;
    handleLoadingDataChange: (key: keyof LoadingData, value: boolean) => void;
    initializeFromRemote: () => Promise<IResult<OrganizationMemberDetailsModel[]>>;
    initializeFromStorage: () => void;
    loadAvailableProducts: () => Promise<ProductModel[]>;
    loadCurrentOrganizationDetails: (organizationId: string) => Promise<IResult<OrganizationModel>>;
    loadInvites: (organizationId: string, searchParams?: ISearchParams) => Promise<IResult<any>>;
    loadOrganizationMembers: (
        organizationId: string,
        searchParams?: ISearchParams
    ) => Promise<IResult<OrganizationMemberModel[]>>;
    loadOrganizations: (updateLicenseAccess?: boolean) => Promise<IResult<OrganizationMemberDetailsModel[]>>;
    removeMember: (organizationId: string, memberId: string) => Promise<IResult<any> | undefined>;
    rotateClientSecret: (organizationId: string, orgAuthClientId: string) => Promise<IResult<any>>;
    searchOrgManageLicenseKeys: (
        organizationId: string,
        searchParams: ISearchParams,
        filter: LicenseKeyFilterTypes
    ) => Promise<IResult<LicenseKeyModel[]>>;
    setCurrentUserEmail: (email: string | null) => void;
    setErrors: (errors: string[]) => void;
    setLoading: (v: boolean) => void;
    setOrgAuthClients: (v: OrganizationAuthClientModel[]) => void;
    setOrgDeviceTypes: (v: DeviceTypeModel[]) => void;
    setScopes: (v: IScopeOption[]) => void;
    updateMemberRole: (
        organizationId: string,
        memberId: string,
        request: UpdateOrganizationMemberRequest
    ) => Promise<IResult<any> | undefined>;
    updateOrganization: (
        organizationId: string,
        request: UpdateOrganizationRequest
    ) => Promise<IResult<OrganizationModel>>;
    updateOrgAuthClient: (
        organizationId: string,
        orgAuthClientId: string,
        request: UpdateAuthClientRequest
    ) => Promise<IResult<OrganizationAuthClientModel>>;
    updateOrgDeviceType: (
        organizationId: string,
        orgDeviceTypeId: string,
        request: UpdateDeviceTypeRequest
    ) => Promise<IResult<DeviceTypeModel>>;
    updateMembershipsByOrganization: (organization: OrganizationModel) => Promise<void>;
    validateLicenseKey: (licenseKey: string) => Promise<IResult<any> | null>;
    validateLicenseKeyForOrganization: (organizationId: string, licenseKey: string) => Promise<IResult<any> | null>;
}

export const useOrganizationStore = create<OrganizationState>()(
    immer((set, get) => {
        const setLoading = (v: boolean) => {
            set({ loading: v });
        };

        const setLoadingData = (key: keyof LoadingData, value: boolean) => {
            set((s) => {
                s.loadingData[key] = value;
            });
        };

        const addError = (message: string) => {
            set((s) => {
                s.errors.push(message);
            });
        };

        const updateMemberships = (
            newMemberships: OrganizationMemberDetailsModel[],
            updateLicenseAccess: boolean = true
        ) => {
            set({ memberships: newMemberships });
            if (updateLicenseAccess) {
                publish('updateUserLicenseAccess');
            }
        };

        const loadOrganizations = async (updateLicenseAccess: boolean = true) => {
            try {
                setLoading(true);
                const response = await enabledApi.getUserOrganizationMemberships();
                if (response.resultType === 'Ok' && response.data) {
                    updateMemberships(response.data, updateLicenseAccess);
                    localStorage.setItem('ORGANIZATIONS', JSON.stringify(response.data));
                }
                setLoading(false);
                return response;
            } catch (error) {
                addError(error.message);
                setLoading(false);
                return new UnexpectedResult<OrganizationMemberDetailsModel[]>();
            }
        };

        const setErrors = (errors: string[]) => set({ errors });
        const setCurrentUserEmail = (email: string | null) => set({ currentUserEmail: email });
        const setScopes = (v: IScopeOption[]) => set({ scopes: v });
        const setOrgAuthClients = (v: OrganizationAuthClientModel[]) => set({ orgAuthClients: v });
        const setOrgDeviceTypes = (v: DeviceTypeModel[]) => set({ orgDeviceTypes: v });
        const handleLoadingDataChange = (key: keyof LoadingData, value: boolean) => setLoadingData(key, value);

        const initializeFromRemote = () => loadOrganizations(false);

        const initializeFromStorage = () => {
            try {
                const localMemberships = localStorage.getItem('ORGANIZATIONS');
                if (localMemberships) {
                    updateMemberships(JSON.parse(localMemberships), false);
                }
            } catch (e) {
                console.log(e);
            }
        };

        const loadOrganizationMembers = async (organizationId: string, searchParams?: ISearchParams) => {
            try {
                setLoading(true);
                const response = await enabledApi.getOrganizationMembers(organizationId, searchParams);
                if (response.data) {
                    set((s) => {
                        const idx = s.organizationMembers.findIndex((m) => m.organizationId === organizationId);
                        if (idx === -1) {
                            s.organizationMembers.push({ organizationId, members: response.data! });
                        } else {
                            s.organizationMembers[idx].members = response.data!;
                        }
                    });
                }
                setLoading(false);
                return response;
            } catch (error) {
                addError(error.message);
                setLoading(false);
                return new UnexpectedResult<OrganizationMemberModel[]>();
            }
        };

        const loadInvites = async (organizationId: string, searchParams?: ISearchParams) => {
            try {
                setLoading(true);
                const response = await enabledApi.getOrganizationInvites(organizationId, searchParams);
                if (response.data) {
                    set((s) => {
                        const idx = s.invites.findIndex((i) => i.organizationId === organizationId);
                        if (idx === -1) {
                            s.invites.push({ organizationId, invites: response.data! });
                        } else {
                            s.invites[idx].invites = response.data!;
                        }
                    });
                }
                setLoading(false);
                return response;
            } catch (error) {
                set({ invites: [] });
                addError(error.message);
                setLoading(false);
                return new UnexpectedResult();
            }
        };

        const createOrganizationInvite = async (organizationId: string, request: InviteOrganizationMemberRequest) => {
            try {
                setLoading(true);
                const response = await enabledApi.createOrganizationInvite(organizationId, request);
                if (response.resultType === 'Ok') {
                    await get().loadInvites(organizationId);
                }
                setLoading(false);
                return response;
            } catch (error) {
                addError(error.message);
                setLoading(false);
            }
        };

        const deleteOrganizationInvite = async (organizationId: string, inviteId: string) => {
            try {
                setLoading(true);
                const response = await enabledApi.deleteOrganizationInvitation(inviteId);
                if (response.resultType === 'Ok') {
                    await get().loadInvites(organizationId);
                }
                setLoading(false);
                return response;
            } catch (error) {
                addError(error.message);
                setLoading(false);
            }
        };

        const acceptOrganizationInvite = async (inviteId: string) => {
            try {
                setLoading(true);
                const response = await enabledApi.acceptOrganizationInvite(inviteId);
                if (response.resultType === 'Ok') {
                    await loadOrganizations();
                }
                setLoading(false);
                return response;
            } catch (error) {
                addError(error.message);
                setLoading(false);
                return new InvalidResult<OrganizationMemberModel>(error.message);
            }
        };

        const removeMember = async (organizationId: string, memberId: string) => {
            try {
                setLoading(true);
                const response = await enabledApi.removeOrganizationMember(memberId);
                if (response.resultType === 'Ok') {
                    await get().loadOrganizationMembers(organizationId);
                    if (response.data?.user.email === get().currentUserEmail) {
                        publish('updateUserLicenseAccess');
                    }
                }
                setLoading(false);
                return response;
            } catch (error) {
                addError(error.message);
                setLoading(false);
            }
        };

        const updateMemberRole = async (
            organizationId: string,
            memberId: string,
            request: UpdateOrganizationMemberRequest
        ) => {
            try {
                setLoading(true);
                const response = await enabledApi.updateOrganizationMember(memberId, request);
                if (response.resultType === 'Ok') {
                    await get().loadOrganizationMembers(organizationId);
                }
                setLoading(false);
                return response;
            } catch (error) {
                addError(error.message);
                setLoading(false);
            }
        };

        const updateOrganization = async (organizationId: string, request: UpdateOrganizationRequest) => {
            try {
                setLoading(true);
                const response = await enabledApi.updateOrganization(organizationId, request);
                if (response.resultType === 'Ok') {
                    await loadOrganizations();
                    return response;
                } else {
                    throw new Error('Error updating organization');
                }
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<OrganizationModel>();
            } finally {
                setLoading(false);
            }
        };

        const getOrganizationDetails = async (organizationId: string) => {
            try {
                setLoading(true);
                const response = await enabledApi.getOrganization(organizationId);
                if (response.resultType !== 'Ok') {
                    throw new Error('Organization not found');
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<OrganizationModel>();
            } finally {
                setLoading(false);
            }
        };

        const loadCurrentOrganizationDetails = async (organizationId: string) => {
            try {
                setLoadingData('currentOrg', true);
                const response = await enabledApi.getOrganization(organizationId);
                if (response.resultType === 'Ok' && response.data) {
                    set({ currentOrg: response.data });
                }
                return response;
            } catch (error) {
                set({ currentOrg: null });
                return new UnexpectedResult<OrganizationModel>();
            } finally {
                setLoadingData('currentOrg', false);
            }
        };

        const getOrgAuthClients = async (organizationId: string) => {
            if (!organizationId) {
                set({ orgAuthClients: [] });
                return new UnexpectedResult<OrganizationAuthClientModel[]>();
            }
            try {
                setLoading(true);
                const response = await enabledApi.getOrgAuthClients(organizationId);
                if (response.resultType === 'Ok' && response.data) {
                    set({ orgAuthClients: response.data });
                } else {
                    set({ orgAuthClients: [] });
                }
                return response;
            } catch (error) {
                addError(error.message);
                set({ orgAuthClients: [] });
                return new UnexpectedResult<OrganizationAuthClientModel[]>();
            } finally {
                setLoading(false);
            }
        };

        const createOrgAuthClient = async (organizationId: string, request: UpdateAuthClientRequest) => {
            if (!organizationId || !request) {
                return new UnexpectedResult<OrganizationAuthClientModel>();
            }
            try {
                setLoading(true);
                const response = await enabledApi.createOrgAuthClient(organizationId, request);
                if (response.resultType === 'Ok') {
                    await get().getOrgAuthClients(organizationId);
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<OrganizationAuthClientModel>();
            } finally {
                setLoading(false);
            }
        };

        const updateOrgAuthClient = async (
            organizationId: string,
            orgAuthClientId: string,
            request: UpdateAuthClientRequest
        ) => {
            if (!organizationId || !orgAuthClientId || !request) {
                return new UnexpectedResult<OrganizationAuthClientModel>();
            }
            try {
                setLoading(true);
                const response = await enabledApi.updateOrgAuthClient(organizationId, orgAuthClientId, request);
                if (response.resultType === 'Ok') {
                    await get().getOrgAuthClients(organizationId);
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<OrganizationAuthClientModel>();
            } finally {
                setLoading(false);
            }
        };

        const rotateClientSecret = async (organizationId: string, orgAuthClientId: string) => {
            if (!organizationId || !orgAuthClientId) {
                return new UnexpectedResult();
            }
            try {
                setLoading(true);
                const response = await enabledApi.rotateOrgAuthClientSecret(organizationId, orgAuthClientId);
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult();
            } finally {
                setLoading(false);
            }
        };

        const deleteOrgAuthClient = async (organizationId: string, orgAuthClientId: string) => {
            if (!organizationId || !orgAuthClientId) {
                return new UnexpectedResult();
            }
            try {
                setLoading(true);
                const response = await enabledApi.deleteOrgAuthClient(organizationId, orgAuthClientId);
                if (response.resultType === 'Ok') {
                    await get().getOrgAuthClients(organizationId);
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult();
            } finally {
                setLoading(false);
            }
        };

        const getOrgDeviceTypes = async (organizationId: string) => {
            if (!organizationId) {
                set({ orgDeviceTypes: [] });
                return new UnexpectedResult<DeviceTypeModel[]>();
            }
            try {
                setLoading(true);
                const response = await enabledApi.getOrgDeviceTypes(organizationId);
                if (response.resultType === 'Ok' && response.data) {
                    set({ orgDeviceTypes: response.data });
                } else {
                    set({ orgDeviceTypes: [] });
                }
                return response;
            } catch (error) {
                addError(error.message);
                set({ orgDeviceTypes: [] });
                return new UnexpectedResult<DeviceTypeModel[]>();
            } finally {
                setLoading(false);
            }
        };

        const createOrgDeviceType = async (organizationId: string, request: UpdateDeviceTypeRequest) => {
            if (!organizationId || !request) {
                return new UnexpectedResult<DeviceTypeModel>();
            }
            try {
                setLoading(true);
                const response = await enabledApi.createOrgDeviceType(organizationId, request);
                if (response.resultType === 'Ok') {
                    await get().getOrgDeviceTypes(organizationId);
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<DeviceTypeModel>();
            } finally {
                setLoading(false);
            }
        };

        const updateOrgDeviceType = async (
            organizationId: string,
            orgDeviceTypeId: string,
            request: UpdateDeviceTypeRequest
        ) => {
            if (!organizationId || !orgDeviceTypeId || !request) {
                return new UnexpectedResult<DeviceTypeModel>();
            }
            try {
                setLoading(true);
                const response = await enabledApi.updateOrgDeviceType(organizationId, orgDeviceTypeId, request);
                if (response.resultType === 'Ok') {
                    await get().getOrgDeviceTypes(organizationId);
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<DeviceTypeModel>();
            } finally {
                setLoading(false);
            }
        };

        const deleteOrgDeviceType = async (organizationId: string, orgDeviceTypeId: string) => {
            if (!organizationId || !orgDeviceTypeId) {
                return new UnexpectedResult();
            }
            try {
                setLoading(true);
                const response = await enabledApi.deleteOrgDeviceType(organizationId, orgDeviceTypeId);
                if (response.resultType === 'Ok') {
                    await get().getOrgDeviceTypes(organizationId);
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult();
            } finally {
                setLoading(false);
            }
        };

        const getScopes = async () => {
            try {
                setLoading(true);
                const response = await enabledApi.getScopes();
                if (response.resultType === 'Ok' && response.data) {
                    return response.data;
                }
                return [];
            } catch (error) {
                addError(error.message);
                return [];
            } finally {
                setLoading(false);
            }
        };

        const updateMembershipsByOrganization = async (organization: OrganizationModel) => {
            const localMemberships = [...get().memberships];
            const index = localMemberships.findIndex((m) => m.organizationId === organization.id);
            if (index > 0) {
                localMemberships[index].organization = organization;
                updateMemberships(localMemberships);
            } else {
                await loadOrganizations();
            }
        };

        const activateNewOrgLicenseKey = async (request: NewOrganizationWithKeyRequest) => {
            try {
                setLoading(true);
                const response = await enabledApi.postNewOrgActivateLicenseKey(request);
                if (response.resultType === 'Ok' && response.data) {
                    await get().updateMembershipsByOrganization(response.data);
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<OrganizationModel>();
            } finally {
                setLoading(false);
            }
        };

        const activateExistingOrgLicenseKey = async (organizationId: string, request: ActivateLicenseKeyRequest) => {
            try {
                setLoading(true);
                const response = await enabledApi.postExistingOrgActivateLicenseKey(organizationId, request);
                if (response.resultType === 'Ok' && response.data) {
                    await get().updateMembershipsByOrganization(response.data);
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<OrganizationModel>();
            } finally {
                setLoading(false);
            }
        };

        const validateLicenseKey = async (licenseKey: string) => {
            try {
                return await enabledApi.validateLicenseKey(licenseKey);
            } catch (error) {
                addError(error.message);
                return null;
            }
        };

        const validateLicenseKeyForOrganization = async (organizationId: string, licenseKey: string) => {
            try {
                const response = await enabledApi.validateLicenseKeyForOrganization(organizationId, licenseKey);
                return response;
            } catch (error) {
                addError(error.message);
                return null;
            }
        };

        const getTrialUpgradeLink = async (organizationId: string, trialId: string) => {
            try {
                setLoading(true);
                const response = await enabledApi.getTrialUpgradeLink(organizationId, trialId);
                return response.resultType == 'Ok' && response.data ? response.data.url : null;
            } catch (error) {
                addError(error.message);
                return null;
            } finally {
                setLoading(false);
            }
        };

        const getTrialUpgradeLinkForProduct = async (organizationId: string, trialId: string, productId: string) => {
            try {
                setLoading(true);
                const response = await enabledApi.getTrialUpgradeLinkForProduct(organizationId, trialId, productId);
                return response.resultType == 'Ok' && response.data ? response.data.url : null;
            } catch (error) {
                addError(error.message);
                return null;
            } finally {
                setLoading(false);
            }
        };

        const getBillingLink = async (organizationId: string) => {
            try {
                setLoading(true);
                const response = await enabledApi.getBillingLink(organizationId);
                return response.resultType == 'Ok' && response.data ? response.data.url : null;
            } catch (error) {
                addError(error.message);
                return null;
            } finally {
                setLoading(false);
            }
        };

        const getBillingLinkForOrgByProduct = async (organizationId: string, productId: string) => {
            try {
                setLoading(true);
                const response = await enabledApi.getBillingLinkForOrgByProduct(organizationId, productId);
                return response.resultType == 'Ok' && response.data ? response.data.url : null;
            } catch (error) {
                addError(error.message);
                return null;
            } finally {
                setLoading(false);
            }
        };

        const getBillingLinkForManagedOrg = async (organizationId: string, managedOrgId: string) => {
            try {
                setLoading(true);
                const response = await enabledApi.getBillingLinkForManagedOrg(organizationId, managedOrgId);
                return response.resultType == 'Ok' && response.data ? response.data.url : null;
            } catch (error) {
                addError(error.message);
                return null;
            } finally {
                setLoading(false);
            }
        };

        const loadAvailableProducts = async () => {
            try {
                setLoading(true);
                const response = await enabledApi.getPublicProducts();
                return response.resultType === 'Ok' && response.data ? response.data : ([] as ProductModel[]);
            } catch (error) {
                addError(error.message);
                return [] as ProductModel[];
            } finally {
                setLoading(false);
            }
        };

        const getOrganizationMembership = (organizationId: string) => {
            if (!organizationId) return undefined;
            return get().memberships.find((member) => member.organizationId === organizationId);
        };

        const searchOrgManageLicenseKeys = async (
            organizationId: string,
            searchParams: ISearchParams,
            filter: LicenseKeyFilterTypes
        ) => {
            try {
                if (filter === 'available') {
                    return get().getOrgManagedLicenseKeysAvailable(organizationId, searchParams);
                } else if (filter === 'used') {
                    return get().getOrgManagedLicenseKeysUsed(organizationId, searchParams);
                } else {
                    return get().getOrgManagedLicenseKeys(organizationId, searchParams);
                }
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<LicenseKeyModel[]>();
            }
        };

        const getOrgManagedLicenseKeys = async (organizationId: string, searchParams: ISearchParams) => {
            try {
                setLoading(true);
                const response = await enabledApi.getOrgManagedLicenseKeys(organizationId, searchParams);
                if (response.resultType === 'Ok' && response.data) {
                    set({ orgManagedLicenseKeys: response.data });
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<LicenseKeyModel[]>();
            } finally {
                setLoading(false);
            }
        };

        const getOrgManagedLicenseKeysAvailable = async (organizationId: string, searchParams?: ISearchParams) => {
            try {
                setLoading(true);
                const response = await enabledApi.getOrgManagedLicenseKeysAvailable(organizationId, searchParams);
                if (response.resultType === 'Ok' && response.data) {
                    set({ orgManagedLicenseKeys: response.data });
                } else {
                    set({ orgManagedLicenseKeys: [] });
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<LicenseKeyModel[]>();
            } finally {
                setLoading(false);
            }
        };

        const getOrgManagedLicenseKeysUsed = async (organizationId: string, searchParams?: ISearchParams) => {
            try {
                setLoading(true);
                const response = await enabledApi.getOrgManagedLicenseKeysUsed(organizationId, searchParams);
                if (response.resultType === 'Ok' && response.data) {
                    set({ orgManagedLicenseKeys: response.data });
                } else {
                    set({ orgManagedLicenseKeys: [] });
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<LicenseKeyModel[]>();
            } finally {
                setLoading(false);
            }
        };

        const getOrgManagedOrgsAll = async (organizationId: string, searchParams?: ISearchParams) => {
            try {
                setLoading(true);
                const response = await enabledApi.getOrgManagedOrgsAll(organizationId, searchParams);
                if (response.resultType === 'Ok' && response.data) {
                    set({ orgManagedOrgs: response.data });
                } else {
                    set({ orgManagedOrgs: [] });
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<OrganizationModel[]>();
            } finally {
                setLoading(false);
            }
        };

        const createManagedOrg = async (organizationId: string, newOrganization: NewOrganizationWithKeyRequest) => {
            try {
                setLoading(true);
                const response = await enabledApi.createManagedOrg(organizationId, newOrganization);
                if (response.resultType === 'Ok' && response.data) {
                    await get().getOrgManagedOrgsAll(organizationId);
                }
                return response;
            } catch (error) {
                addError(error.message);
                return new UnexpectedResult<OrganizationModel>();
            } finally {
                setLoading(false);
            }
        };

        const getOrganizationProducts = async (
            organizationId: string,
            locale: string,
            searchParams?: ISearchParams
        ) => {
            if (!organizationId) {
                set({ orgProducts: [] });
                return new UnexpectedResult<OrganizationPrivateProductModel[]>();
            }
            try {
                setLoading(true);
                const response = await enabledApi.getOrgProducts(organizationId, locale, searchParams);
                if (response.resultType === 'Ok' && response.data) {
                    set({ orgProducts: response.data });
                } else {
                    set({ orgProducts: [] });
                }
                return response;
            } catch (error) {
                addError(error.message);
                set({ orgProducts: [] });
                return new UnexpectedResult<OrganizationPrivateProductModel[]>();
            } finally {
                setLoading(false);
            }
        };

        const getOrgAvailableCustomControls = async (organizationId: string, locale: string) => {
            if (!organizationId) {
                set({ orgAvailableCustomControls: [] });
                return false;
            }
            let newAvailableCustomControls: AvailableCustomControlsModel[] = [];
            try {
                setLoading(true);
                const response = await get().getOrgDeviceTypes(organizationId);
                if (response.resultType === 'Ok' && response.data) {
                    for (const deviceType of response.data) {
                        const controlsResponse = await enabledApi.getDeviceTypeCustomControls(deviceType.id, locale);
                        if (controlsResponse.resultType === 'Ok' && controlsResponse.data) {
                            if (controlsResponse.data.controls && controlsResponse.data.controls.length > 0) {
                                newAvailableCustomControls.push({
                                    deviceId: deviceType.id,
                                    deviceTypeId: deviceType.id,
                                    name: deviceType.name,
                                    controls: controlsResponse.data.controls.filter(
                                        (control) => control.isProfileControl
                                    ),
                                });
                            }
                        }
                    }
                    set({ orgAvailableCustomControls: newAvailableCustomControls });
                } else {
                    set({ orgAvailableCustomControls: [] });
                }
                return true;
            } catch (error) {
                addError(error.message);
                set({ orgAvailableCustomControls: [] });
                return false;
            } finally {
                setLoading(false);
            }
        };

        const getOrganizationProductLink = async (organizationId: string, productId: string) => {
            try {
                setLoading(true);
                const response = await enabledApi.getOrgProductLink(organizationId, productId);
                return response.resultType == 'Ok' && response.data ? response.data.url : null;
            } catch (error) {
                addError(error.message);
                return null;
            } finally {
                setLoading(false);
            }
        };

        return {
            // State
            currentOrg: null,
            currentOrgId: null,
            currentUserEmail: null,
            errors: [],
            invites: [],
            loading: true,
            loadingData: {
                organizationMembers: false,
                organizations: false,
                projects: false,
                licenseKeys: false,
                invites: false,
                scopes: false,
                memberships: false,
                currentOrg: false,
                managedOrgs: false,
                managedKeys: false,
                products: false,
            },
            memberships: [],
            organizationMembers: [],
            orgAuthClients: [],
            orgAvailableCustomControls: [],
            orgDeviceTypes: [],
            orgManagedLicenseKeys: [],
            orgManagedOrgs: [],
            orgProducts: [],
            scopes: [],

            // Actions
            initializeFromStorage,
            acceptOrganizationInvite,
            activateExistingOrgLicenseKey,
            activateNewOrgLicenseKey,
            createManagedOrg,
            createOrganizationInvite,
            createOrgAuthClient,
            createOrgDeviceType,
            deleteOrganizationInvite,
            deleteOrgAuthClient,
            deleteOrgDeviceType,
            getBillingLink,
            getBillingLinkForManagedOrg,
            getBillingLinkForOrgByProduct,
            getOrganizationDetails,
            getOrganizationMembership,
            getOrganizationProductLink,
            getOrganizationProducts,
            getOrgAuthClients,
            getOrgAvailableCustomControls,
            getOrgDeviceTypes,
            getOrgManagedLicenseKeys,
            getOrgManagedLicenseKeysAvailable,
            getOrgManagedLicenseKeysUsed,
            getOrgManagedOrgsAll,
            getScopes,
            getTrialUpgradeLink,
            getTrialUpgradeLinkForProduct,
            handleLoadingDataChange,
            initializeFromRemote,
            loadAvailableProducts,
            loadCurrentOrganizationDetails,
            loadInvites,
            loadOrganizationMembers,
            loadOrganizations,
            removeMember,
            rotateClientSecret,
            searchOrgManageLicenseKeys,
            setCurrentUserEmail,
            setErrors,
            setLoading,
            setOrgAuthClients,
            setOrgDeviceTypes,
            setScopes,
            updateMemberRole,
            updateMembershipsByOrganization,
            updateOrganization,
            updateOrgAuthClient,
            updateOrgDeviceType,
            validateLicenseKey,
            validateLicenseKeyForOrganization,
        };
    })
);

// ---- Module-level subscription replacing useEffect ----
subscribe('userUpdated', (data: any) => {
    useOrganizationStore.getState().setCurrentUserEmail(data.user.email);
});
